import React, { Ref, useEffect, useImperativeHandle } from 'react'
import {
  Modal,
  StyleSheet,
  Animated,
  TouchableWithoutFeedback,
  LayoutChangeEvent,
  View,
  GestureResponderEvent,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Color, Spacing } from '../../style'
import { Button, ButtonProps } from '../button'
import { Text } from '../typography'

const BackgroundColor = 'rgba(0, 0, 0, 0.5)'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: BackgroundColor,
  },
  bottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: Color.simplyWhite,
  },
  content: {
    padding: Spacing.S,
  },
  title: {
    textAlign: 'center',
  },
  button: {
    marginTop: Spacing.XS,
  },
})

export interface Props {
  title?: string
  buttons?: ButtonProps[]
  onOpen?: () => void
  onClose?: () => void
  test_ref?: Ref<any>
}

const FeedbackBottom = (props: Props) => {
  const { title, buttons, onClose, onOpen, test_ref } = props
  const [visible, setVisible] = React.useState(true)
  const [height, setHeight] = React.useState(0)
  const opacity = React.useRef(new Animated.Value(0)).current
  const safeareaInsets = useSafeAreaInsets()

  useImperativeHandle(test_ref, () => ({
    layout: (event: LayoutChangeEvent) => {
      layout(event)
    },
  }))

  useEffect(() => {
    if (height === 0) return
    Animated.timing(opacity, {
      toValue: visible ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start(() => {
      if (!visible) {
        onClose && onClose()
      } else {
        onOpen && onOpen()
      }
    })
  }, [visible, height])

  const layout = (event: LayoutChangeEvent) => {
    const size = event.nativeEvent.layout
    setHeight(size.height)
  }

  const backdropStyle = { opacity }
  const bottomStyle = {
    transform: [
      {
        translateY: opacity.interpolate({
          inputRange: [0, 1],
          outputRange: [height, 0],
        }),
      },
    ],
  }
  const contentStyle = {
    marginBottom: safeareaInsets.bottom,
  }
  return (
    <Modal transparent style={styles.container}>
      <TouchableWithoutFeedback onPress={() => setVisible(false)} testID="FeedbackBottom.Backdrop">
        <Animated.View style={[styles.backdrop, backdropStyle]} />
      </TouchableWithoutFeedback>
      <Animated.View style={[styles.bottom, bottomStyle]} onLayout={layout} testID="FeedbackBottom.Content">
        <View style={[styles.content, contentStyle]}>
          {title && (
            <Text level={3} style={styles.title}>
              {title}
            </Text>
          )}
          {buttons?.map((buttonProps, index) => {
            const { onPress, ...otherButtonProps } = buttonProps
            const close = (event: GestureResponderEvent) => {
              setVisible(false)
              onPress && onPress(event)
            }
            return (
              <View key={index} style={styles.button}>
                <Button onPress={close} {...otherButtonProps} />
              </View>
            )
          })}
        </View>
      </Animated.View>
    </Modal>
  )
}

export default FeedbackBottom
